<template>
  <div>
    <div class="columns">
      <div class="column is-two-thirds">
        <p class="title is-3">{{appnum}}</p>
        <p class="subtitle is-5" v-if="!loading"></p>
        <p class="subtitle is-5" v-else>Refreshing bank data. This will take a minute or two...</p>
      </div>
      <div
        class="column"
        style="text-align: right;"
        v-if="accounts.length > 0 && !decisionUI && userdata"
      >
        <p>Logged in as {{userdata.name}}</p>
        <a
          class="button is-medium is-info"
          @click="refreshAccounts"
          :class="loading ? 'is-loading' : ''"
        >
          <span class="icon">
            <i class="fas fa-retweet"></i>
          </span>
          <span>Refresh Accounts</span>
        </a>
      </div>
      <div style="margin: auto; margin-right: 50px;" v-if="!userdata">
        <p
          style="font-variant-caps: all-small-caps; font-size: 0.9rem;text-align: right; color: grey;"
        >Read Only Mode</p>
      </div>
    </div>
    <div class="columns">
      <transition name="fade" mode="out-in">
        <div class="column" key="accounts" v-if="!decisionUI">
          <p
            v-if="accounts.length > 0"
            class="subtitle is-5"
          >{{accounts.length > 0 ? 'Linked Accounts ('+accounts.length+')' : 'No accounts'}}</p>
          <table class="table is-hoverable" v-if="!loading && accounts.length > 0">
            <thead>
              <tr>
                <th>Bank</th>
                <th>Account</th>
                <th>Name</th>
                <th>Balance</th>
                <th>Refreshed</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="account in accs" v-bind:key="account.accountid">
                <th>{{account.institution}}</th>
                <th>{{account.accnum}}</th>
                <th>{{account.name}}</th>
                <th>${{numberWithCommas(account.balance, true)}}</th>
                <th>{{formatUnixDate(account.timestamp)}}</th>
                <th>
                  <a
                    class="button is-small is-success"
                    :disabled="account.statements < 1 || account.statements == null"
                    @click="downloadStatements(account)"
                    :class="downloadLoading ? 'is-loading': ''"
                  >
                    <span class="icon is-small">
                      <i class="fas fa-download"></i>
                    </span>
                    <span v-if="account.mfa && account.mfa == true">MFA Required</span>
                    <span v-else-if="account.statements > 0">Statements ({{account.statements}})</span>
                    <span v-else>No Statements</span>
                  </a>
                </th>
                <th>
                  <a class="button is-small is-link" @click="viewAggregation(account)">
                    <span class="icon is-small">
                      <i class="fas fa-database"></i>
                    </span>
                    <span>Transactions</span>
                  </a>
                </th>
              </tr>
            </tbody>
          </table>
          <div v-if="plaid" style="margin-bottom: 30px;">
            <p class="subtitle is-5">Plaid Statements - Pulled {{plaid_refreshed}}</p>
            <div class="file" v-for="plaidacc in plaid_accounts" v-bind:key="plaidacc.account_id">
              <div class="columns">
                <div class="column is-one-quarter">
                  <p>{{plaidacc.mask}} ({{plaidacc.name}})</p>
                  <p>${{plaidacc.balances.current}}</p>
                </div>
                <div class="column">
                  <div class="buttons">
                    <a
                      class="button is-small is-primary"
                      target="_blank"
                      v-for="month in plaid_dates"
                      v-bind:key="plaidacc.account_id+''+month.in"
                      :href="'/business/docgenerator/statements/'+plaid.items[0].item_id+'/mnLY7M3u9jwUnq64DVYX/'+plaidacc.account_id+'/'+month.in"
                    >{{month.display}}</a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="columns">
            <div class="column">
              <p
                class="subtitle is-5"
              >{{reports.length > 0 ? 'Credit Reports ('+reports.length+')' : 'No Credit Reports'}}</p>
              <table class="table is-hoverable" v-if="!loading">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>FICO</th>
                    <th>Pulled</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pg in reports" v-bind:key="pg.timestamp">
                    <th>{{pg.name}}</th>
                    <th>{{pg.score}}</th>
                    <th>{{formatUnixDate(pg.timestamp)}}</th>
                    <th>
                      <a class="button is-small is-info" @click="viewReport(pg)">
                        <span class="icon is-small">
                          <i class="fas fa-table"></i>
                        </span>
                        <span>Full Report</span>
                      </a>
                    </th>
                  </tr>
                </tbody>
              </table>
              <div v-if="userdata">
                <p class="subtitle is-5">Decisions</p>
                <button
                  class="button is-success is-outlined"
                  @click="decisionToggle"
                  v-if="!decisionUI"
                >
                  <span class="icon is-small">
                    <i class="fas fa-plus"></i>
                  </span>
                  <span>New</span>
                </button>
              </div>
              <div v-else>
                <p class="subtitle is-5">Decisions</p>
                <button class="button is-success is-outlined" @click="loginPrompt">
                  <span class="icon is-small">
                    <i class="fas fa-sign-in-alt"></i>
                  </span>
                  <span>Login to Add Decision</span>
                </button>
              </div>
            </div>
            <div class="column">
              <p class="subtitle is-5">Files ({{files.length}})</p>
              <div class="file" v-for="file in files" v-bind:key="file.id+'file'">
                <div class="columns">
                  <div class="column">
                    <p>{{file.type_name}} ({{file.fileextension}})</p>
                    <p class="is-size-7">Uploaded {{formatUnixDate(file.sent)}}</p>
                  </div>
                  <div class="column">
                    <a
                      class="button is-small is-success is-pulled-right"
                      style="margin-right: 10px; margin-top: 10px;"
                      :href="file.gsurl"
                      target="_blank"
                    >
                      <span class="icon is-small">
                        <i class="fas fa-download"></i>
                      </span>
                      <span>Download</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="column" key="decision" v-if="decisionUI">
          <decisionwizard
            :appnum="appnum"
            :accounts="accounts"
            :reports="reports"
            :token="token"
            :userdata="userdata"
            v-if="decisionUI"
            @goBack="decisionUI = false"
          ></decisionwizard>
          <!-- <button class="button is-danger" @click="decisionToggle" style="margin-top: 20px;">
            <span>Cancel</span>
          </button>-->
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import $ from "jquery";
import decisionwizard from "@/components/aggregation/decisionwizard";
import moment from "moment";
export default {
  name: "AccountSelector",
  components: {
    decisionwizard: decisionwizard
  },
  props: {
    appnum: String,
    accounts: Array,
    reports: Array,
    decisions: Array,
    pass: Object,
    custid: Number,
    token: String,
    userdata: Object,
    files: Array,
    plaidmodel: String
  },
  data() {
    return {
      loading: false,
      downloadLoading: false,
      aggregation: {},
      accs: this.accounts,
      report: {},
      decisionUI: false,
      plaid: null
    };
  },
  computed: {
    plaid_accounts: function() {
      if (this.plaid) {
        var accounts = []
        for (var i = 0; i < this.plaid.items[0].accounts.length; i++){
          var ac = this.plaid.items[0].accounts[i]
          if (ac.type && ac.type == 'depository'){
            accounts.push(ac)
          }
        }
        return accounts;
      }
      return null;
    },
    plaid_refreshed: function() {
      if (!this.plaid) {
        return;
      }
      return moment(this.plaid.date_generated).format("llll");
    },
    plaid_dates: function() {
      if (!this.plaid) {
        return;
      }
      var arr = [];
      var starting_moment = moment(this.plaid.date_generated);
      var months = {};
      months[starting_moment.format("YYYY-MM")] = {
        display: starting_moment.format("MMM YYYY") + " (MTD)",
        in: starting_moment.format("YYYY-MM")
      };
      for (var i = 0; i < this.plaid.days_requested; i++) {
        starting_moment = starting_moment.subtract(1, "days");
        console.log(starting_moment.format("l"));
        if (months[starting_moment.format("YYYY-MM")] == undefined)
          months[starting_moment.format("YYYY-MM")] = {
            display: starting_moment.format("MMM YYYY"),
            in: starting_moment.format("YYYY-MM")
          };
      }
      for (var month in months) {
        arr.push(months[month]);
      }
      return arr.reverse();
    }
  },
  methods: {
    formatUnixDate(ts) {
      console.log("formatting", ts);
      return moment.unix(ts).format("l LT");
    },
    numberWithCommas(val, money = false) {
      if (money) val = parseFloat(val).toFixed(2);
      var returnval = null;
      if (val != null)
        returnval = val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      if (returnval == "NaN") returnval = "0.00";

      return returnval;
    },
    next() {
      this.$emit("next", this.$data);
      console.log(this.$data);
    },
    doubleNext() {
      this.$emit("doublenext", this.$data);
    },
    decisionToggle() {
      this.decisionUI = !this.decisionUI;
    },
    loginPrompt() {
      var self = this;
      let auth = prompt("Enter your auth key");
      $.ajax({
        type: "POST",
        url: "/api/aggregation/authenticate",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          token: auth
        })
      })
        .done(function(response) {
          console.log(response);
          self.token = auth;
          localStorage.setItem("authtoken", auth);
          self.userdata = response.userdata;
        })
        .fail(function(err) {
          console.log(err.responseText);
          alert(err.responseText);
        });
    },
    downloadStatements(account) {
      var self = this;
      if (self.downloadLoading) {
        return;
      }
      if (account.statements < 1) {
        return;
      }
      self.downloadLoading = true;
      fetch(
        "/api/aggregation/download-statements/" +
          account.custid +
          "-" +
          account.accountid +
          ".zip"
      )
        .then(resp => resp.blob())
        .then(blob => {
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.style.display = "none";
          a.href = url;
          // the filename you want
          a.download = "statements.zip";
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          self.downloadLoading = false;
        })
        .catch(() => {
          alert(
            "There was an error downloading statements. Try again in a few seconds."
          );
          self.downloadLoading = false;
        });
    },
    viewAggregation(account) {
      var self = this;
      $.ajax({
        type: "POST",
        url: "/api/aggregation/get-aggregation",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          custid: account.custid,
          timestamp: account.timestamp,
          accountid: account.accountid
        })
      })
        .done(function(response) {
          console.log(response);
          self.aggregation = { resp: response, account: account };
          self.next();
        })
        .fail(function(err) {
          console.log(err.responseText);
          alert(err.responseText);
        });
    },
    viewReport(pg) {
      var self = this;
      self.report = pg.json;
      self.doubleNext();
    },
    getAccounts() {
      var self = this;
      self.loading = true;
      $.ajax({
        type: "POST",
        url: "/api/aggregation/get-accounts",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({
          link: this.$route.params.code,
          pass: this.pass.value,
          appnum: this.appnum
        })
      })
        .done(function(response) {
          console.log(response.accounts);
          self.accs = response.accounts;
          // self.custid = response.custid
          self.loading = false;
        })
        .fail(function(err) {
          alert(err.responseText);
          self.invalid = true;
        });
    },
    refreshAccounts() {
      if (this.loading) {
        return;
      }
      console.log(this.custid);
      this.loading = true;
      var self = this;
      $.ajax({
        type: "POST",
        url: "/api/aggregation/refresh-customer",
        contentType: "application/json",
        dataType: "json",
        data: JSON.stringify({ custid: self.custid, token: self.token })
      })
        .done(function(response) {
          console.log(response);
          self.getAccounts();
        })
        .fail(function(err) {
          console.log(err.responseText);
          alert(err.responseText);
          self.loading = false;
        });
    }
  },
  mounted: function() {
    let r = this.plaidmodel;
    if (r) {
      this.plaid = JSON.parse(r);
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.file {
  width: 100%;
  display: block;
  background: #ebf7ff;
  margin-bottom: 15px;
  border-radius: 5px;
  padding: 10px 10px;
}
.file p {
  margin-bottom: 2px;
}
</style>
